import * as React from "react";

/**
 * This queue is for writing and updating tables. Particularly where we need this is when you're
 * creating a "complex" object that has multiple tables that need to be updated. For example,
 * when you create a new hangar, you need to create a new hangar, new tenants.
 *
 * This is a work in progress. The idea is that you can add a function to the queue, and it will
 * run in the background. If you add a function that depends on another function, it will wait
 * until that function is done before running. This way, you can add a function to create a new
 * hangar, and then add a function to create a new tenant, and it will wait until the hangar is
 * created before creating the tenant.
 */

type FunctionType = () => Promise<void> | Promise<any>;

export const useFunctionQueue = () => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [functions, setFunctions] = React.useState<FunctionType[]>([]);

  React.useEffect(() => {
    async function callFunctions() {
      const functionsToCall = functions.slice(currentIndex);
      // Execute sequentially to prevent foreign key constraint violations
      for (const fn of functionsToCall) {
        try {
          await fn();
        } catch (error) {
          console.error("Error executing queued function:", error);
          // Continue with other functions even if one fails
        }
      }
      setCurrentIndex(functions.length);
    }

    callFunctions();
  }, [currentIndex, functions]);

  function addFunctions(newFunctions: FunctionType[]) {
    setFunctions((prevFunctions) => [...prevFunctions, ...newFunctions]);
  }

  function addFunction(newFunction: FunctionType) {
    setFunctions((prevFunctions) => [...prevFunctions, newFunction]);
  }

  function next() {
    setCurrentIndex((prevIndex) => prevIndex + 1);
  }

  return { addFunctions, addFunction, next };
};
